/**
 * Message / call module constants. Use this file for shared literals and small helpers
 * so hooks, screens, and utils stay consistent.
 */

import { bootstrapOutgoingCallRoute } from '../../../../shared/services/call/callRouteBootstrap';
import {
  clearOutgoingPlaceCallBlock,
  setPendingCallMediaError,
} from '../../../../shared/services/call/callMediaAccessGate';

/** Default when URL has no `type=` query for call UI */
export const DEFAULT_CALL_UI_MEDIA_TYPE = 'video';

export const CALL_MEDIA_TYPE = {
  VOICE: 'voice',
  VIDEO: 'video',
};

/** Platform: 1 credit = $0.50 */
export const CREDITS_PER_USD = 2;

/** Normalize arbitrary call kind input to `'voice' | 'video'`. */
export const normalizeCallKind = (callKind) =>
  String(callKind || '').toLowerCase() === CALL_MEDIA_TYPE.VIDEO
    ? CALL_MEDIA_TYPE.VIDEO
    : CALL_MEDIA_TYPE.VOICE;

export const isVideoCallKind = (callKind) => normalizeCallKind(callKind) === CALL_MEDIA_TYPE.VIDEO;

/** Billing / API payload: `'voice' | 'video'` from the voice-call UI flag */
export const getCallMediaTypeFromVoiceUi = (isVoiceCallUi) =>
  isVoiceCallUi ? CALL_MEDIA_TYPE.VOICE : CALL_MEDIA_TYPE.VIDEO;

/** From query `type`, summary `callUiType`, or similar (defaults to video when unset). */
export const getCallKindFromCallUiType = (callUiType) =>
  String(callUiType || '').toLowerCase() === CALL_MEDIA_TYPE.VOICE
    ? CALL_MEDIA_TYPE.VOICE
    : CALL_MEDIA_TYPE.VIDEO;

/**
 * Navigate to voice or video call route after acquiring media under the user gesture.
 * @param {import('../../../../shared/utils/useNavigation').default} navigate
 * @param {'voice'|'video'} callKind
 * @param {Record<string, unknown>} [callOpts]
 * @returns {Promise<{ ok: boolean, error?: object }>}
 */
export const navigateToCallRoute = async (navigate, callKind, callOpts = {}) => {
  const result = await bootstrapOutgoingCallRoute(callKind);
  if (!result?.ok) {
    setPendingCallMediaError(result?.error || null);
  } else {
    clearOutgoingPlaceCallBlock();
  }

  if (isVideoCallKind(callKind)) {
    navigate.toVideo(callOpts);
  } else {
    navigate.toCall(callOpts);
  }
  return result || { ok: false };
};

// --- CallingScreen timing ---

export const ICE_DISCONNECT_GRACE_MS = 3000;
/** Must exceed matrix-js-sdk ICE_DISCONNECTED_TIMEOUT (30s) so we do not hang up before the SDK can recover. */
export const MAX_ICE_DISCONNECT_MS = 32 * 1000;
export const OFFLINE_HANGUP_MS = 30 * 1000;
export const FEEDS_DEBOUNCE_MS = 150;
export const BANDWIDTH_SWITCH_COOLDOWN_MS = 5000;
export const BANDWIDTH_CHECK_INTERVAL_MS = 10 * 1000;

// --- Matrix `com.juicyhouse.call_event` payloads ---

/** Shared payload version for custom call events sent on the room timeline */
export const JUICYHOUSE_CALL_EVENT_VERSION = 1;

/** Action strings for {@link JUICYHOUSE_CALL_EVENT_VERSION} payloads */
export const CALL_EVENT_ACTION = {
  STARTED: 'started',
  SUBSCRIBER_LOW_CREDITS: 'subscriber_low_credits',
  /** Subscriber is about to / has committed to auto-hangup; creator uses as backup if m.call.hangup is delayed or missed. */
  SUBSCRIBER_INSUFFICIENT_CREDITS_HANGUP: 'subscriber_insufficient_credits_hangup',
  CAMERA_FLIP: 'camera_flip',
};

/** After a successful front/back switch, block rapid re-flips so Matrix/MediaHandler can settle (avoids frozen video). */
export const CAMERA_FLIP_COOLDOWN_MS = 2500;

/** Placeholder when `activeCall.callId` is missing for effect dependency keys */
export const CALL_ID_MISSING_PLACEHOLDER = '__no_call_id__';

/** Subscriber wallet deduct: fallback key segment when `activeCall` is already cleared */
export const ROOM_SCOPED_CALL_KEY_PREFIX = 'room:';
export const UNKNOWN_ROOM_ID_SEGMENT = 'unknown';

/** Defer showing creator low-balance banner to next task; subscriber event already fires at the real warning threshold. */
export const CREATOR_NOTIFY_DELAY_MS = 0;

/** App-level call end reason (not a matrix-js-sdk CallErrorCode). Bills actual connected time only. */
export const CALL_ENDED_REASON_INSUFFICIENT_CREDITS = 'insufficient_credits';
